import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';

import OpenInNewIcon from '@material-ui/icons/OpenInNew';

import FMPHeadline from '../../components/ui/FMPHeadline';
import FMPSectionHeader from '../../components/ui/FMPSectionHeader';
import EnhancedTable from '../../components/table/EnhancedTable';
import SystemContainer from '../../containers/system/System';
import { search } from '../../modules/system/supportedDocuments';
import { getSharedStyles } from '../../themes';

export const isLocalhost = Boolean(window.location.hostname === 'localhost');

const styles = (theme) => ({
	...getSharedStyles(theme),
	note: {
		fontSize: 13,
		color: '#666',
		marginBottom: 12,
	},
	noSample: {
		color: '#999',
	},
});

const columns = [
	{ sortId: 'name', displayId: 'name', label: 'Document' },
	{ sortId: 'vendor', displayId: 'vendor', label: 'Vendor' },
	{ sortId: 'format', displayId: 'format', label: 'Type' },
	{ sortId: 'arrivesVia', displayId: 'arrivesVia', label: 'Arrives Via' },
	{ sortId: 'hasSample', displayId: 'sampleDisplay', label: 'Latest Version' },
	{ sortId: 'notes', displayId: 'notes', label: 'Notes' },
];

class SupportedDocuments extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			rows: undefined,
			isLoading: true,
		};
	}

	componentDidMount() {
		search({
			callback: (err, r) => {
				if (err) {
					this.setState({ isLoading: false });
					return this.props.showErrors(_.isArray(err) ? err : [err]);
				}
				this.setState({
					isLoading: false,
					rows: _.get(r, ['collections', 'supportedDocumentCollection'], []),
				});
			},
		});
	}

	//- The sample is served by a GET so the browser can open it in a new tab. Same
	//- host/request pattern as the CSV download buttons (see RailcarSearch).
	getSampleUrl = (key) => {
		const { accessToken } = this.props;

		const request = {
			method: 'supportedDocument.download',
			params: { key, accessToken },
		};

		let host = '';
		if (isLocalhost) {
			host = 'http://localhost:3700';
		}
		return `${host}/api?request=${encodeURIComponent(JSON.stringify(request))}`;
	};

	buildRow = (row) => {
		const { classes } = this.props;

		return {
			key: row.key,
			sortIndex: row.sortIndex,
			name: row.name,
			vendor: row.vendor,
			format: row.format,
			arrivesVia: row.arrivesVia,
			notes: row.notes || '',
			hasSample: row.hasSample ? 1 : 0,
			sampleDisplay: row.hasSample ? (
				<a
					href={this.getSampleUrl(row.key)}
					target="_blank"
					rel="noreferrer noopener"
					style={{ whiteSpace: 'nowrap' }}
				>
					View sample <OpenInNewIcon style={{ fontSize: 14, verticalAlign: 'middle' }} />
				</a>
			) : (
				<span className={classes.noSample}>No sample on file</span>
			),
		};
	};

	renderCategory = (category, categoryRows) => {
		const { classes } = this.props;

		return (
			<Paper className={classes.paper} key={category} style={{ marginBottom: 20 }}>
				<FMPSectionHeader label={category} isTop={true} />
				<EnhancedTable
					isDense={true}
					columns={columns}
					rows={categoryRows.map(this.buildRow)}
					defaultOrder={'asc'}
					defaultOrderBy={'sortIndex'}
				/>
			</Paper>
		);
	};

	render() {
		const { classes } = this.props;
		const { rows, isLoading } = this.state;

		//- Preserve the registry's category order rather than sorting alphabetically.
		const categories = _.uniq(_.map(rows, 'category'));
		const rowsByCategory = _.groupBy(rows, 'category');

		return (
			<div>
				<FMPHeadline label="Supported Vendor Documents" />
				<div className={classes.note}>
					Every vendor document the FMP can ingest, across the AP ingestion system, the AR/OCR
					delivery-document pipeline, railcar importers and email reports. Each sample is the latest
					version of the document we support &mdash; either the fixture the parser&apos;s own tests
					run against, or the most recent document of that type pulled from production.
				</div>

				{isLoading && <div className={classes.fetching}>Loading supported documents...</div>}
				{!isLoading && _.isEmpty(rows) && (
					<div className={classes.fetching}>Could not load the supported document list.</div>
				)}

				{categories.map((category) => this.renderCategory(category, rowsByCategory[category]))}
			</div>
		);
	}
}

SupportedDocuments.propTypes = {
	classes: PropTypes.object.isRequired,
	accessToken: PropTypes.string.isRequired,
	showErrors: PropTypes.func.isRequired,
};

export default _.flow([withStyles(styles), SystemContainer])(SupportedDocuments);
